import { ref, computed } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import type { Ref } from 'vue'

// Types
import type {
  SubtestEntity,
  QuestionEntity,
  OptionsEntity,
  Track,
  ManagedTimeout
} from '@/customTypes'

// Stores
import { useDataStore } from '@/stores/DataStore'
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'

// Services
import { ThreadPool } from '@/services/ThreadPoolService'
import AxiosInstance from '@/services/AxiosInstance.js'
import Logger from '@/services/Logger.js'

// Helpers
import { useTimeout } from '@/utils/helpers'

interface PreloadResult {
  priorityPromises: Promise<void | void[]>[]
  regularPromises: Promise<void | void[]>[]
}

export const usePreloadStore = defineStore('preload', () => {
  const threadPool = new ThreadPool(_THREAD_POOL_LIMIT_, _THREAD_POOL_MB_LIMIT_)

  const dataStore = useDataStore()
  const {
    screenerTemplateId,
    avatarVolumeLevel,
    backgroundVolumeLevel,
    screenerData,
    pupilAnswers,
    currentSubtest,
    currentQuestion
  } = storeToRefs(dataStore)

  const logger: any = new Logger('PreloadStore', true)
  const soundEffectsStore = useSoundEffectsStore()
  const { setManagedTimeout, clearManagedTimeout } = useTimeout()

  const preloadedImages: Ref<{ [key: string]: any }> = ref({})

  const loadingAssets = ref<{ [key: string]: { data?: string | Track; loaded: boolean } }>({})
  const stagedSubtestOptions: Ref<{ [key: string]: OptionsEntity[] | string[] }> = ref({})
  const stagedSubtestOptionsLoading: Ref<boolean> = ref(false)

  const preloadTimeout = 5000

  const preloadProgress = computed(() => {
    const total = Object.keys(loadingAssets.value).length
    const loaded = Object.values(loadingAssets.value).filter(
      (asset) => asset.loaded === true
    ).length
    if (total === 0) return 0
    return Math.round((loaded / total) * 100)
  })

  function getFailedAsset(fileName: string): string | Track | undefined {
    return loadingAssets.value[fileName]?.data
  }

  const isAssetLoading = (key: string) => {
    return loadingAssets.value[key] && !loadingAssets.value[key].loaded
  }

  const preloadNextSubtest = async () => {
    if (currentSubtest.value) {
      const nextSubtest = dataStore.getNextSubtest(currentSubtest.value.index + 1)
      if (nextSubtest) {
        await preloadSubtestAssets(nextSubtest)
        stagedSubtestOptionsLoading.value = false
      }
    }
  }

  const preloadSubtestById = async (value: number) => {
    const subtest = dataStore.getNextSubtest(value)
    if (subtest) {
      await preloadSubtestAssets(subtest)
      stagedSubtestOptionsLoading.value = false
    }
  }

  function loadNextQuestionAssets() {
    if (!stagedSubtestOptions.value) return
    const prefix = currentQuestion.value!.isPracticeQuestion ? 'p' : ''
    let nextId = `${prefix}${currentQuestion.value!.index + 1}`

    const isLastQuestion: boolean = dataStore.checkIfLastQuestion(
      currentQuestion.value as QuestionEntity,
      currentSubtest.value as SubtestEntity
    )

    if (currentQuestion.value!.isPracticeQuestion && isLastQuestion) {
      nextId = '0'
    }

    const folder = 'question-assets'
    const option = stagedSubtestOptions.value[nextId]

    if (option) {
      logger.info('Loading next question Images', nextId, option)
      const promises = option.map((optionItem) => {
        const imageUrl =
          typeof optionItem === 'string' ? optionItem : (optionItem.imageUrl as string)
        const filePath = `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/${folder}`
        return preloadImageAssets(filePath, imageUrl)
      })

      Promise.all(promises)
        .then(() => {
          delete stagedSubtestOptions.value[nextId]
        })
        .catch((error) => {
          logger.error('Error preloading images:', error)
        })
    }
  }

  /**
   * Preloads an audio asset and adds it to the sound effects store.
   * @param {string} fileName - The name of the audio file to be loaded.
   * @param {string} folderName - The folder where the audio file is located within the Screeners S3 Bucket.
   * @param {string} audioName - The name to assign to the audio track.
   * @param {number} [volume=1] - The volume level of the audio track.
   * @param {boolean} [loop=false] - Whether the audio track should loop.
   * @returns {Promise<void>} A promise that resolves when the audio asset is loaded and added to the store.
   */
  async function preloadAudioAsset(
    fileName: string,
    folderName: string,
    audioName: string,
    volume: number = 1,
    loop: boolean = false
  ) {
    return new Promise<void>(async (resolve, reject) => {
      if (isAssetLoading(fileName) || !fileName) return resolve()
      const baseUrl = `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}`
      const audioUrl = `${baseUrl}/${folderName}/${fileName}?v=${_ASSETS_VERSION_}`
      const audioTrackData = {
        id: fileName.split('.')[0],
        name: audioName,
        url: audioUrl,
        volume: volume,
        loop: loop
      }
      try {
        updateLoadedAssets(fileName, { data: audioTrackData, loaded: false })
        await soundEffectsStore.createPlayerForTrack(audioTrackData)
        updateLoadedAssets(fileName, { loaded: true })
        resolve()
      } catch (error) {
        updateLoadedAssets(fileName, { loaded: true })
        reject(error)
      }
    })
  }

  /**
   * Preloads a generic asset and marks it as loaded. Uses recursion to handle viseme files.
   * @param {string} fileName - The name of the asset file to be loaded.
   * @param {string} folderName - The folder where the asset file is located.
   * @param {string} [visemeFolder=''] - The folder where the viseme file is located (optional).
   * @returns {Promise<void>} A promise that resolves when the asset is loaded.
   */
  function preloadAsset(fileName: string, folderName: string, visemeFolder: string = '') {
    return new Promise<void>((resolve, reject) => {
      const baseUrl = `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}`
      const assetUrl = `${baseUrl}/${folderName}/${fileName}?v=${_ASSETS_VERSION_}`
      updateLoadedAssets(fileName, { data: assetUrl, loaded: false })

      AxiosInstance.get(assetUrl)
        .then(() => {
          if (visemeFolder) {
            const visemeFileName = fileName.split('.')[0] + '.json'
            preloadAsset(visemeFileName, visemeFolder, '')
              .then(() => {
                updateLoadedAssets(fileName, { loaded: true })
                resolve()
              })
              .catch(reject)
          } else {
            updateLoadedAssets(fileName, { loaded: true })
            resolve()
          }
        })
        .catch((error: any) => {
          updateLoadedAssets(fileName, { loaded: true })
          logger.info('error loading assets', error)
          reject(error)
        })
    })
  }

  /**
   * Fetches metadata for filesize of an image file using Axios.
   * @param {string} imageUrl - The URL of the image file.
   * @returns {Promise<number>} A promise resolving to the size of the image in bytes.
   */
  async function fetchImageSize(imageUrl: string): Promise<number> {
    const defaultSize = 100 * 1024 * 1024 // default error sizes to 100mb
    try {
      const imageTimestampURL = `${imageUrl}?timestamp=${new Date().getTime()}`
      const response = await AxiosInstance.head(imageTimestampURL)

      const contentLength = parseInt(response.headers['content-length'], 10)
      if (!isNaN(contentLength)) {
        return contentLength
      } else {
        logger.info('Content-Length header not found or invalid. Setting default size.')
        return defaultSize
      }
    } catch (error) {
      logger.error('Error fetching image size. Setting default size:', error)
      return defaultSize
    }
  }

  /**
   * Preloads an image asset and marks it as loaded.
   * @param {string} imagePath - The path where the image file is located.
   * @param {string} fileName - The name of the image file to be loaded.
   * @returns {Promise<void>} A promise that resolves when the image is loaded.
   */
  async function preloadImageAssets(imagePath: string, fileName: string): Promise<void> {
    if (screenerData.value?.metadata?.skipPreloading?.includes('images')) return

    const imageUrl = `${imagePath}/${fileName}`
    const imageSize = await fetchImageSize(imageUrl)

    return threadPool.add(
      () =>
        new Promise((resolve, reject) => {
          const img = new Image()
          img.src = `${imageUrl}?v=${_ASSETS_VERSION_}`
          img.classList.add('no-select')

          updateLoadedAssets(fileName, { data: imageUrl, loaded: false })
          img.onload = () => {
            updateLoadedAssets(fileName, { loaded: true })
            preloadedImages.value[fileName] = img
            resolve()
          }

          img.onerror = (error) => {
            logger.error('Error loading image:', fileName, error)
            updateLoadedAssets(fileName, { loaded: true })
            reject(error)
          }
        }),
      imageSize
    )
  }

  /**
   * Preloads animation assets and their associated image assets if necessary.
   * @param {string} animationFileName - The name of the animation file to be loaded.
   * @param {string} [folderName='subtest-backgrounds'] - The folder where the animation file is located (default is 'subtest-backgrounds').
   * @returns {Promise<void>} A promise that resolves when the animation assets are loaded.
   */
  async function preloadAnimationAssets(
    animationFileName: string,
    folderName: string = 'subtest-backgrounds'
  ): Promise<void> {
    if (isAssetLoading(animationFileName) || !animationFileName) return

    const baseUrl = `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}`

    if (!String(animationFileName).endsWith('.json')) {
      return preloadImageAssets(`${baseUrl}/${folderName}`, animationFileName)
    }

    const lastIndexOfPath = animationFileName.lastIndexOf('/')
    const filePath =
      lastIndexOfPath !== -1 ? animationFileName.slice(0, lastIndexOfPath) : animationFileName

    try {
      const response = await AxiosInstance.get(
        `${baseUrl}/${folderName}/${animationFileName}?v=${_ASSETS_VERSION_}`
      )
      if (!response) {
        updateLoadedAssets(animationFileName, { loaded: true })
        return
      }

      const animationAssetKeys = Object.keys(response.assets)

      const assetPromises = animationAssetKeys.map(async (key, index) => {
        const asset = response.assets[key]
        if (asset.p) {
          const assetName = `${asset.u}${asset.p}`
          try {
            await withTimeout(
              preloadImageAssets(`${baseUrl}/${folderName}/${filePath}`, assetName),
              preloadTimeout,
              `Preload ${index} timed out`
            )
          } catch (error) {
            updateLoadedAssets(assetName, { loaded: true })
          }
        }
      })

      await Promise.all(assetPromises)
    } catch (error) {
      updateLoadedAssets(animationFileName, { loaded: true })
      logger.error('error loading animation asset', error)
    }
  }

  /**
   * Preloads assets for a given question.
   * @param {QuestionEntity} question - The question entity containing assets to be preloaded.
   * @returns {Promise<void[]>} A promise that resolves when all assets are preloaded.
   */
  async function preloadAssets(question: QuestionEntity) {
    const current = currentQuestion.value!
    if (!current || !current.screenerPupilSubtestId) return
    const fieldToFolders = {
      introUrl: 'avatar-audio',
      assetUrl: 'question-assets',
      questionUrl: 'avatar-audio',
      promptUrl: 'avatar-audio',
      outroUrl: 'avatar-audio',
      options: 'question-assets',
      answerGivenOutroUrl: 'avatar-audio'
    }

    const preloadPromises: Promise<void>[] = []

    const sameSubtest = question.screenerPupilSubtestId === current.screenerPupilSubtestId

    // Skip preloading assets for questions that have already been answered
    if (sameSubtest) {
      const isCurrentAPractice = current.isPracticeQuestion
      const isNextAPractice = question.isPracticeQuestion
      const isOlderQuestion = isCurrentAPractice
        ? isNextAPractice && question.index < current.index
        : sameSubtest && question.index < current.index
      if (!isCurrentAPractice && isNextAPractice) return
      if (isOlderQuestion) return
    }

    for (const [field, folder] of Object.entries(fieldToFolders)) {
      const fieldKey = field as keyof QuestionEntity
      const assetUrl = question[fieldKey] as string

      if (isAssetLoading(assetUrl) || !assetUrl || assetUrl === 'random') continue

      if (fieldKey === 'options') {
        preloadQuestionOptions(question, folder, preloadPromises)
      } else {
        preloadQuestionAssets(question, fieldKey, assetUrl, folder, preloadPromises)
      }
    }

    await Promise.all(preloadPromises)
  }

  /**
   * Preloads options assets for a given question or adds question images to a staged loading queue.
   * @param {QuestionEntity} question - The question entity containing options assets to be preloaded.
   * @param {string} folder - The folder where the assets are stored.
   * @param {Promise<void>[]} preloadPromises - The array of preload promises.
   */
  function preloadQuestionOptions(
    question: QuestionEntity,
    folder: string,
    preloadPromises: Promise<void>[]
  ) {
    const prefix = question.isPracticeQuestion ? 'p' : ''
    const stagedId = `${prefix}${question.index}`

    if (stagedSubtestOptionsLoading.value) {
      logger.info('STAGED LOADING OPTIONS FOR QUESTION IMAGES')
      stagedSubtestOptions.value[stagedId] = question.options as OptionsEntity[]
    } else {
      logger.info('PRELOADING IMAGES FOR OPTIONS')
      stagedSubtestOptionsLoading.value = true
      const filePath = `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/${folder}`
      for (const option of question.options as OptionsEntity[]) {
        if (option.imageUrl) {
          preloadPromises.push(preloadImageAssets(filePath, option.imageUrl))
        }
      }
    }
  }

  /**
   * Preloads non-options assets for a given question.
   * @param {QuestionEntity} question - The question entity containing assets to be preloaded.
   * @param {keyof QuestionEntity} fieldKey - The key of the field in the question entity.
   * @param {string} assetUrl - The URL of the asset to be preloaded.
   * @param {string} folder - The folder where the asset is stored.
   * @param {Promise<void>[]} preloadPromises - The array of preload promises.
   */
  function preloadQuestionAssets(
    question: QuestionEntity,
    fieldKey: keyof QuestionEntity,
    assetUrl: string,
    folder: string,
    preloadPromises: Promise<void>[]
  ) {
    if (folder.endsWith('-audio') || assetUrl.endsWith('.webm')) return

    if (assetUrl.endsWith('.png')) {
      preloadQuestionImage(question, fieldKey, assetUrl, folder, preloadPromises)
    } else {
      logger.info('PRELOADING VISEME')
      preloadPromises.push(
        preloadAsset(assetUrl, folder, folder === 'avatar-audio' ? 'avatar-viseme' : '')
      )
    }
  }

  /**
   * Preloads image assets for a given question or adds question images to a staged loading queue.
   * @param {QuestionEntity} question - The question entity containing image assets to be preloaded.
   * @param {keyof QuestionEntity} fieldKey - The key of the field in the question entity.
   * @param {string} assetUrl - The URL of the image asset to be preloaded.
   * @param {string} folder - The folder where the image asset is stored.
   * @param {Promise<void>[]} preloadPromises - The array of preload promises.
   */
  function preloadQuestionImage(
    question: QuestionEntity,
    fieldKey: keyof QuestionEntity,
    assetUrl: string,
    folder: string,
    preloadPromises: Promise<void>[]
  ) {
    const prefix = question.isPracticeQuestion ? 'p' : ''
    const stagedId = `${prefix}${question.index}`

    if (stagedSubtestOptionsLoading.value) {
      stagedSubtestOptions.value[stagedId] = [assetUrl]
      logger.info('STAGED LOADING QUESTION IMAGE')
    } else {
      logger.info('PRELOADING IMAGE FOR QUESTION')
      stagedSubtestOptionsLoading.value = true
      const filePath = `${_ASSETS_BUCKET_URL_}/${screenerTemplateId.value}/${folder}`
      preloadPromises.push(preloadImageAssets(filePath, assetUrl))
    }
  }

  /**
   * Preloads assets for a given subtest.
   * @param {SubtestEntity} subtest - The subtest entity containing assets to be preloaded.
   * @param {boolean} [awaitPreload=false] - Whether to await the preloading of assets before returning.
   * @returns {Promise<void | void[]>} A promise that resolves when all assets are preloaded.
   */
  async function preloadSubtestAssets(
    subtest: SubtestEntity,
    awaitPreload = false
  ): Promise<void | void[]> {
    const practiceQuestions = subtest.practiceQuestions || []
    const questions = subtest.questions || []
    const preloadPromises = [].map((p, index) =>
      withTimeout(p, preloadTimeout, `Subtest asset preload ${index} timed out`)
    )

    if (subtest.metadata?.bgAudio) {
      preloadPromises.push(
        preloadAudioAsset(
          subtest.metadata?.bgAudio,
          'tracks-and-sound-effects',
          `${subtest.title} background music`,
          backgroundVolumeLevel.value,
          true
        )
      )
    }
    if (subtest.metadata?.backgroundImage) {
      preloadPromises.push(preloadAnimationAssets(subtest.metadata?.backgroundImage))
    }

    practiceQuestions.forEach((question) => {
      preloadPromises.push(preloadAssets(question))
    })
    questions.forEach((question) => {
      preloadPromises.push(preloadAssets(question))
    })

    if (awaitPreload) {
      updateLoadedAssets(`${subtest.title}-assets`, { loaded: false })
      return Promise.all(preloadPromises)
        .then(() => {
          updateLoadedAssets(`${subtest.title}-assets`, { loaded: true })
        })
        .catch(() => {
          updateLoadedAssets(`${subtest.title}-assets`, { loaded: true })
        })
    } else {
      return Promise.all(preloadPromises) as Promise<void[]>
    }
  }

  function withTimeout<T>(promise: Promise<T>, timeout: number, errorMsg: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = ref<ManagedTimeout>(
        setManagedTimeout(() => {
          reject(new Error(errorMsg))
        }, timeout)
      )

      promise
        .then((res) => {
          clearManagedTimeout(timer)
          resolve(res)
        })
        .catch((err) => {
          clearManagedTimeout(timer)
          reject(err)
        })
    })
  }

  /**
   * Preloads all necessary assets for the screener data. Prioritizes high-priority assets to load first.
   * @returns {Promise<void>} A promise that resolves when all screener data assets are preloaded.
   */
  async function preloadScreenerData() {
    if (!screenerData.value) return
    const metadata = screenerData.value?.metadata

    // Offset by one which is handled after promises have finished to avoid instant 100% progress
    updateLoadedAssets('init', { loaded: false })

    try {
      // Separate the high-priority preloads
      const priorityPreloads: Promise<void | void[]>[] = [].map((p, index) =>
        withTimeout(p, preloadTimeout, `Priority preload ${index} timed out`)
      )
      priorityPreloads.push(preloadAnimationAssets(screenerData.value.metadata?.intro?.background))

      const preloadPromises: Promise<void | void[]>[] = [].map((p, index) =>
        withTimeout(p, preloadTimeout, `Preload ${index} timed out`)
      )

      if (
        metadata?.outro?.background &&
        metadata?.intro?.background !== metadata?.outro?.background
      ) {
        priorityPreloads.push(preloadAnimationAssets(metadata.outro.background))
      }

      if (metadata?.rewardBackground) {
        priorityPreloads.push(preloadAnimationAssets(metadata?.rewardBackground))
      }

      if (metadata?.outro?.url) {
        priorityPreloads.push(
          preloadAudioAsset(metadata?.outro?.url, 'avatar-audio', 'outro', avatarVolumeLevel.value)
        )
      }
      if (metadata?.outro?.bgAudio) {
        preloadPromises.push(
          preloadAudioAsset(
            metadata?.outro?.bgAudio,
            'tracks-and-sound-effects',
            'Outro Audio',
            backgroundVolumeLevel.value,
            true
          )
        )
      }
      if (metadata?.rewardScene) {
        // set reward scene audio or set default
        const rewardSceneAudio = `${metadata?.rewardBackground ? metadata?.rewardBackground?.split('.')[0] : 'RewardScreen'}_audio.mp3`
        preloadPromises.push(
          preloadAudioAsset(
            rewardSceneAudio,
            'tracks-and-sound-effects',
            'Chests background Track',
            0.05,
            true
          )
        )

        preloadPromises.push(
          preloadAudioAsset(
            'reward-step-0.mp3',
            'tracks-and-sound-effects',
            'Chest appearance audio',
            backgroundVolumeLevel.value,
            false
          )
        )

        preloadPromises.push(
          preloadAudioAsset(
            'reward-step-1.mp3',
            'tracks-and-sound-effects',
            'Chests lock rattle audio',
            backgroundVolumeLevel.value,
            false
          )
        )

        preloadPromises.push(
          preloadAudioAsset(
            'reward-step-2.mp3',
            'tracks-and-sound-effects',
            'Chests unlock audio and stars',
            backgroundVolumeLevel.value,
            false
          )
        )
      }
      if (metadata?.questionReward) {
        preloadPromises.push(
          preloadAudioAsset(
            metadata?.questionReward,
            'tracks-and-sound-effects',
            'Question Reward',
            0.2
          )
        )
      }
      if (metadata?.selectionSound) {
        preloadPromises.push(
          preloadAudioAsset(
            metadata?.selectionSound,
            'tracks-and-sound-effects',
            'Selection sound effect',
            0.2
          )
        )
      }

      if (metadata?.levelUpAudio) {
        preloadPromises.push(
          preloadAudioAsset(
            metadata.levelUpAudio,
            'tracks-and-sound-effects',
            'Chests animation audio',
            0.8
          )
        )
      }

      // If intro already played, wait until first subtest is loaded.
      if (pupilAnswers.value.length > 0) {
        logger.info('No need to pre-load intro, preloading subtest instead')
        const nextSubtest = dataStore.getNextSubtest(currentSubtest.value?.index || 0)
        if (nextSubtest) {
          logger.info('SETTING UP PRELOAD FOR SUBTEST', currentSubtest.value?.index)
          priorityPreloads.push(preloadSubtestAssets(nextSubtest, true))
        }
      } else {
        const { priorityPromises, regularPromises } = preloadIntroAssets(metadata)
        priorityPreloads.push(...priorityPromises)
        preloadPromises.push(...regularPromises)
      }

      await Promise.all(priorityPreloads).then(() => {
        priorityPreloads.forEach((item, index) => {
          updateLoadedAssets(`priority_${index}`, { loaded: true })
        })
      })

      await Promise.all(preloadPromises).then(() => {
        preloadPromises.forEach((item, index) => {
          updateLoadedAssets(`preload_${index}`, { loaded: true })
        })
      })
      stagedSubtestOptionsLoading.value = false
      logger.info('Preloading complete', loadingAssets.value)
    } catch (error) {
      logger.error('Error during preloading:', error)
    }
    updateLoadedAssets('init', { loaded: true })
  }

  const deleteImageFromCache = (key: string) => {
    if (preloadedImages.value[key]) {
      delete preloadedImages.value[key]
    }
  }

  const updateLoadedAssets = (
    asset: string,
    update: { data?: string | Track; loaded: boolean }
  ) => {
    loadingAssets.value[asset] = loadingAssets.value[asset]
      ? { ...loadingAssets.value[asset], ...update }
      : update
  }

  function preloadIntroAssets(metadata?: any): PreloadResult {
    const priorityPromises: Promise<void | void[]>[] = []
    const regularPromises: Promise<void | void[]>[] = []

    if (!metadata) {
      metadata = screenerData.value?.metadata
    }

    if (!metadata) {
      logger.warn('No metadata available for preloading intro assets')
      return { priorityPromises, regularPromises }
    }

    logger.info('Preloading intro assets')

    if (metadata.intro) {
      if (Array.isArray(metadata.intro.url)) {
        metadata.intro.url.forEach((introUrl: string) => {
          priorityPromises.push(
            preloadAudioAsset(introUrl, 'avatar-audio', 'intro', avatarVolumeLevel.value)
          )
        })
      } else if (metadata.intro.url) {
        priorityPromises.push(
          preloadAudioAsset(metadata.intro.url, 'avatar-audio', 'intro', avatarVolumeLevel.value)
        )
      }
    }

    if (metadata.intro?.bgAudio) {
      regularPromises.push(
        preloadAudioAsset(
          metadata.intro.bgAudio,
          'tracks-and-sound-effects',
          'Intro Background',
          backgroundVolumeLevel.value,
          true
        )
      )
    }

    if (currentSubtest.value) {
      priorityPromises.push(preloadSubtestAssets(currentSubtest.value, true))
    }

    return { priorityPromises, regularPromises }
  }

  // Helper function for standalone use
  async function preloadIntroAssetsStandalone(): Promise<void> {
    const { priorityPromises, regularPromises } = preloadIntroAssets()
    try {
      await Promise.all([...priorityPromises, ...regularPromises])
      logger.info('Intro assets preloaded successfully')
    } catch (error) {
      logger.error('Error preloading intro assets:', error)
      throw error
    }
  }
  return {
    preloadProgress,
    preloadedImages,
    preloadSubtestById,
    getFailedAsset,
    preloadAudioAsset,
    preloadAsset,
    preloadImageAssets,
    preloadAnimationAssets,
    preloadAssets,
    preloadSubtestAssets,
    preloadNextSubtest,
    preloadScreenerData,
    preloadIntroAssetsStandalone,
    deleteImageFromCache,
    loadNextQuestionAssets
  }
})
